import { Building2, Mail, Phone, MapPin, Palette, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const ProfileStep = ({ data, navigate }) => (
  <div className="space-y-6">
    <div className="text-center mb-6">
      <div className="bg-gradient-to-br from-teal-100 to-teal-200 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
        <Building2 className="h-8 w-8 text-teal-600" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Complete Your Clinic Profile</h2>
      <p className="text-gray-600">
        Add essential information about your clinic to personalize your experience
      </p>
    </div>

    {/* Clinic Information Card */}
    <Card className="border-2">
      <CardContent className="pt-6">
        <h3 className="font-semibold text-gray-900 mb-4">Clinic Information</h3>
        
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Building2 className="h-5 w-5 text-teal-600 mt-1" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-700">Clinic Name</p>
              <p className="text-gray-600">{data?.name || 'Not set'}</p>
            </div>
            {data?.name ? (
              <Badge className="bg-green-100 text-green-800">Complete</Badge>
            ) : (
              <Badge variant="outline">Required</Badge>
            )}
          </div>

          <div className="flex items-start gap-3">
            <Mail className="h-5 w-5 text-teal-600 mt-1" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-700">Contact Email</p>
              <p className="text-gray-600">{data?.contact_email || 'Not set'}</p>
            </div>
            {data?.contact_email ? (
              <Badge className="bg-green-100 text-green-800">Complete</Badge>
            ) : (
              <Badge variant="outline">Required</Badge>
            )}
          </div>

          <div className="flex items-start gap-3">
            <Phone className="h-5 w-5 text-teal-600 mt-1" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-700">Phone Number</p>
              <p className="text-gray-600">{data?.contact_phone || 'Not set'}</p>
            </div>
            {data?.contact_phone && (
              <Badge className="bg-green-100 text-green-800">Complete</Badge>
            )}
          </div>

          <div className="flex items-start gap-3">
            <MapPin className="h-5 w-5 text-teal-600 mt-1" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-700">Address</p>
              <p className="text-gray-600">{data?.address || 'Not set'}</p>
            </div>
            {data?.address && (
              <Badge className="bg-green-100 text-green-800">Complete</Badge>
            )}
          </div>

          <div className="flex items-start gap-3">
            <Palette className="h-5 w-5 text-teal-600 mt-1" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-700">Branding Colors</p>
              <div className="flex gap-2 mt-1">
                <div 
                  className="w-8 h-8 rounded border-2 border-gray-300"
                  style={{ backgroundColor: data?.settings?.primary_color || '#14b8a6' }}
                  title="Primary Color"
                />
                <div 
                  className="w-8 h-8 rounded border-2 border-gray-300"
                  style={{ backgroundColor: data?.settings?.secondary_color || '#0d9488' }}
                  title="Secondary Color"
                />
              </div>
            </div>
            <Badge className="bg-blue-100 text-blue-800">Optional</Badge>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t">
          <Button 
            onClick={() => navigate('/admin/settings')}
            className="w-full bg-teal-600 hover:bg-teal-700"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Go to Settings to Update
          </Button>
        </div>
      </CardContent>
    </Card>

    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <p className="text-sm text-blue-800">
        <strong>💡 Pro Tip:</strong> Complete profile information helps patients find and trust your clinic. You can always update these details later from Settings.
      </p>
    </div>
  </div>
);

export default ProfileStep;
